/**
 * A compact, newest-first list of a host's instructions with a status chip and a
 * link to the detail/log page. The host pages are the one place this list lives —
 * the home page surfaces only the in-flight / last-failed banners, so an operator
 * who wants the scroll comes here.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Chip, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { narrationFor, type HostRole, type InstructionDoc, type InstructionStatus } from '@finvari/deployer';
import { CAPTION_TICK_MS, useInstructions, useNow } from '../hooks';
import { timelineText } from '../lib/instructionActivity';

const STATUS_COLOR: Record<InstructionStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
    pending: 'default',
    running: 'info',
    succeeded: 'success',
    failed: 'error',
    cancelled: 'warning',
    rejected: 'error',
};

/**
 * `initialVisible` collapses a long history to its most recent entries so the
 * hosts page doesn't grow to a full screen of rows; the rest stay one "Show more"
 * click away. Omit it to render every fetched row.
 */
export function InstructionList({
    role,
    limit,
    initialVisible,
}: {
    role: HostRole;
    limit: number;
    initialVisible?: number;
}) {
    const { value: rows, loading } = useInstructions(role, limit);
    const now = useNow(CAPTION_TICK_MS);
    const [expanded, setExpanded] = useState(false);

    // "None yet" is a finding about this host's history; before the first snapshot
    // there is no finding, only an empty array that looks like one.
    if (loading) {
        return (
            <Typography variant="body2" color="text.secondary">
                Loading…
            </Typography>
        );
    }

    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No instructions yet.
            </Typography>
        );
    }

    const collapsed = initialVisible !== undefined && !expanded;
    const visibleRows = collapsed ? rows.slice(0, initialVisible) : rows;
    const hiddenCount = rows.length - visibleRows.length;

    return (
        <>
        <List dense disablePadding>
            {visibleRows.map((row) => (
                <ListItem
                    key={row.id}
                    disablePadding
                    secondaryAction={<Chip size="small" label={row.status} color={STATUS_COLOR[row.status]} />}
                >
                    <ListItemButton component={RouterLink} to={`/instructions/${role}/${row.id}`}>
                        <ListItemText
                            primary={titleOf(row)}
                            secondary={
                                <>
                                    {[row.createdBy?.email, timelineText(row, now)].filter(Boolean).join(' · ')}
                                    {outcomeText(row) && (
                                        <Typography component="span" variant="caption" display="block" color="error">
                                            {outcomeText(row)}
                                        </Typography>
                                    )}
                                </>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                        />
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
        {(hiddenCount > 0 || expanded) && initialVisible !== undefined && (
            <Button size="small" onClick={() => setExpanded((prev) => !prev)} sx={{ mt: 1 }}>
                {expanded ? 'Show less' : 'Show ' + hiddenCount + ' more'}
            </Button>
        )}
        </>
    );
}

/**
 * The host's reason for a rejection or failure, if the instruction has one. A bare
 * "rejected" chip sends the operator into the log to learn why; the one-line
 * reason belongs in the row.
 */
function outcomeText(row: InstructionDoc): string | undefined {
    return row.rejectedReason ?? row.error;
}

function titleOf(row: InstructionDoc): string {
    try {
        return narrationFor(row.kind).title;
    } catch {
        return row.kind;
    }
}
